/**
 * useEntityEvent (AC-1369).
 *
 * React subscription wrapper around entityEvents. Calls the handler whenever
 * an event matching `entity` (and optionally `id`) lands. Most consumers want
 * to pin both - "tell me when MY queue changes" - so id is the common case.
 *
 * The handler is wrapped in a ref so callers don't have to memoize it. They
 * can pass an inline closure and the effect won't re-subscribe on every
 * render. This matters because the components that need this hook (queue
 * nodes inside React Flow) re-render constantly.
 */
import { useEffect, useRef } from 'react';
import {
  EntityEvent,
  EntityType,
  onEntityEvent,
} from '../services/entityEvents';

interface UseEntityEventOptions {
  /** Only fire for this entity type. */
  entity: EntityType;
  /**
   * Optional id filter. When set, the handler only fires for events whose
   * id matches. Compared as strings so number/string mixups don't drop
   * events silently.
   */
  id?: number | string | null | undefined;
  /** Handler invoked with the matching event. */
  handler: (event: EntityEvent) => void;
}

export function useEntityEvent({ entity, id, handler }: UseEntityEventOptions): void {
  // Stash the latest handler in a ref so the subscription effect below can
  // run once per (entity, id) change instead of resetting every render.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const targetId = id != null ? String(id) : null;
    const unsubscribe = onEntityEvent((event) => {
      if (event.entity !== entity) return;
      if (targetId !== null && String(event.id) !== targetId) return;
      handlerRef.current(event);
    });
    return unsubscribe;
  }, [entity, id]);
}
